import type { PostContent } from '../api/createPost'
import { INSTAGRAM_RESTRICTIONS } from './restrictions'

const ALLOWED_IMAGE_FORMATS = INSTAGRAM_RESTRICTIONS.image.allowedFormats as readonly string[]
const ALLOWED_VIDEO_FORMATS = INSTAGRAM_RESTRICTIONS.video.allowedFormats as readonly string[]

const rejectionReason = (type: string) => {
  if (type.startsWith('image/')) {
    return `Instagram does not accept ${type} images. Please convert to JPEG or PNG.`
  }
  if (type.startsWith('video/')) {
    return `Instagram does not accept ${type} videos. Please convert to MP4 or MOV.`
  }
  return `Instagram does not accept files of type ${type}. Only images and videos are supported.`
}

export const postContent = (
  uploadUrls: { url: string; file: { type: string } }[],
  caption: string,
  isStory: boolean,
): PostContent | { type: 'invalid'; reason: string } => {
  if (uploadUrls.length === 0)
    return { type: 'invalid', reason: 'Instagram posts must have at least one image or video file' }

  // Validate caption length
  if (caption && caption.length > INSTAGRAM_RESTRICTIONS.caption.maxLength) {
    return {
      type: 'invalid',
      reason: `Caption too long (${caption.length}/${INSTAGRAM_RESTRICTIONS.caption.maxLength} characters)`,
    }
  }

  if (caption) {
    const hashtagCount = (caption.match(/#[\p{L}\p{N}_]+/gu) ?? []).length
    if (hashtagCount > INSTAGRAM_RESTRICTIONS.caption.maxHashtags) {
      return {
        type: 'invalid',
        reason: `Too many hashtags (${hashtagCount}/${INSTAGRAM_RESTRICTIONS.caption.maxHashtags} max). Instagram suppresses distribution for posts above this limit.`,
      }
    }
  }

  if (uploadUrls.length === 1) {
    const fileType = uploadUrls[0]!.file.type
    if (ALLOWED_IMAGE_FORMATS.includes(fileType)) {
      return {
        type: isStory ? 'imageStory' : 'image',
        url: uploadUrls[0]!.url,
        caption,
      }
    }
    if (ALLOWED_VIDEO_FORMATS.includes(fileType)) {
      return {
        type: isStory ? 'videoStory' : 'reel',
        url: uploadUrls[0]!.url,
        caption,
      }
    }
    return { type: 'invalid', reason: rejectionReason(fileType) }
  }

  // Stories only support a single photo or video — fail loudly instead of
  // silently demoting to a feed carousel.
  if (isStory) {
    return {
      type: 'invalid',
      reason: 'Instagram stories only support a single photo or video',
    }
  }

  // Validate carousel item count
  if (uploadUrls.length > INSTAGRAM_RESTRICTIONS.carousel.maxItems) {
    return {
      type: 'invalid',
      reason: `Too many items (${uploadUrls.length}/${INSTAGRAM_RESTRICTIONS.carousel.maxItems} max)`,
    }
  }

  for (const { file } of uploadUrls) {
    if (!ALLOWED_IMAGE_FORMATS.includes(file.type) && !ALLOWED_VIDEO_FORMATS.includes(file.type)) {
      return { type: 'invalid', reason: rejectionReason(file.type) }
    }
  }

  return {
    type: 'carousel',
    urls: uploadUrls.map(({ file, url }) => ({
      type: ALLOWED_IMAGE_FORMATS.includes(file.type) ? 'image' : 'video',
      url: url,
    })),
    caption,
  }
}
